import {
  TutorialJoyrideTooltip,
  getTutorialJoyrideStyles,
} from "../index.js";
import { numberPhaseSteps } from "../phaseSteps.js";

export const REPORTS_TUTORIAL_PARAM = "tutorial";
export const REPORTS_TUTORIAL_VALUE = "reports";
export const REPORTS_TUTORIAL_MOCK_ID = "tutorial-report";
export const REPORTS_TUTORIAL_INTRO = {
  title: "Antes de empezar",
  description:
    "Este tutorial te enseña a revisar un informe y a enviarlo al cliente.",
  badge: "Ejemplo guiado",
  highlightTitle: "Vas a recorrer un informe de ejemplo",
  highlightDescription:
    "No se modifica ni se envía ningún informe real. Primero verás cómo se organiza el listado y después abriremos un informe de ejemplo para explicarte cada bloque y cuándo aparece.",
  bulletTitle: "Durante el tutorial",
  bullets: [
    "Conocerás el listado de informes, sus estados y las acciones rápidas.",
    "Recorrerás las secciones del informe: resumen, tráfico, visibilidad, rendimiento web, proyectos, detalles del periodo e información interna.",
    "Terminarás aprobando el informe de ejemplo, que es el paso que lo hace visible para el cliente.",
  ],
  note:
    "Al salir del tutorial podrás revisar, ajustar y aprobar tus informes reales con el mismo criterio.",
  confirmLabel: "Ver tutorial",
};

export const REPORTS_TUTORIAL_COMPLETION_DESCRIPTION =
  "Has completado el tutorial de informes. Ya sabes cómo revisar cada bloque, ajustar los textos mientras el informe está en revisión y aprobarlo para que el cliente lo reciba.";

export const REPORTS_TABLE_STEPS = [
  {
    target: '[data-tutorial="reports-table"]',
    title: "Informes",
    content:
      "Aquí tienes los informes de tus clientes. El día 1 de cada mes se genera automáticamente el del mes anterior, y con \"Generar informe\" puedes crear uno para cualquier rango de fechas. Puedes buscar por cliente y filtrar por periodo en los filtros avanzados.",
    placement: "auto",
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="reports-status-column"]',
    title: "Estado del informe",
    content:
      "Cada informe nace en \"Revisión\": solo lo ve el equipo y todavía puedes ajustarlo. Cuando lo apruebas pasa a \"Enviado\" y el cliente ya puede consultarlo. Usa las pestañas Todos, Revisión y Enviados para ver qué tienes pendiente.",
    placement: "bottom",
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="reports-actions-column"]',
    title: "Acciones rápidas",
    content:
      "Desde el menú de cada fila puedes \"Aprobar y enviar\" un informe sin abrirlo, o \"Marcar en revisión\" uno ya enviado para retirarlo temporalmente al cliente. Lo habitual es abrirlo antes para revisarlo: pulsa Siguiente y abriremos un informe de ejemplo.",
    placement: "bottom",
    disableBeacon: true,
  },
];

const detailStep = (target, title, content) => ({
  target: `[data-tutorial="${target}"]`,
  title,
  content,
  placement: "auto",
  disableBeacon: true,
  scrollBlock: "start",
});

export const REPORT_DETAIL_STEPS = [
  detailStep(
    "report-summary-anchor",
    "Resumen ejecutivo",
    "El informe empieza con un resumen redactado con IA y una franja con lo más destacado de cada sección. Mientras el informe está en revisión puedes editar el texto con \"Editar\"."
  ),
  detailStep(
    "report-traffic-anchor",
    "Tráfico y conversiones",
    "Si el cliente tiene Google Analytics conectado verás usuarios, sesiones y conversiones del periodo frente al periodo anterior de la misma duración, su evolución diaria, canales, dispositivos y objetivos, con una lectura de la IA."
  ),
  detailStep(
    "report-visibility-anchor",
    "Visibilidad en Google",
    "Con Search Console (y SE Ranking, si está conectado) el informe muestra clics, impresiones, CTR y posición media, las búsquedas principales y las páginas más visitadas."
  ),
  detailStep(
    "report-performance-anchor",
    "Rendimiento web",
    "Las puntuaciones de PageSpeed en móvil y ordenador, con su variación respecto a la auditoría anterior. Cada sección de datos solo aparece si esa conexión está configurada."
  ),
  detailStep(
    "report-projects-anchor",
    "Proyectos y tareas",
    "Los proyectos con actividad en el periodo, con su progreso. Despliega cada uno para ver las tareas por fase."
  ),
  detailStep(
    "report-content-anchor",
    "Contenidos",
    "Las piezas de contenido que el cliente aprobó en el periodo: cuántas, si vienen de campañas o son individuales, para qué redes y cuándo se aprobaron."
  ),
  detailStep(
    "report-details-anchor",
    "Detalles del periodo",
    "El relato detallado de todo el periodo: resultados de la web, trabajo en proyectos, contenidos y acuerdos con el cliente, redactado con IA. Igual que el resumen, puedes editarlo antes de aprobar el informe."
  ),
  detailStep(
    "report-internal-anchor",
    "Información interna",
    "Mensajes intercambiados, tickets y tiempo invertido. Solo lo ven consultores y administradores: el cliente no recibe este bloque."
  ),
  {
    target: '[data-tutorial="report-send"]',
    title: "Aprobar informe",
    content:
      "Cuando hayas revisado el informe, apruébalo con este botón: pasará a \"Enviado\", será visible para el cliente y este recibirá una notificación. Pulsa Siguiente para terminar el tutorial (no se envía nada real).",
    placement: "bottom-end",
    disableBeacon: true,
  },
];

const REPORTS_TUTORIAL_SEQUENCE = [
  ["table", REPORTS_TABLE_STEPS],
  ["detail", REPORT_DETAIL_STEPS],
];

export const REPORTS_TABLE_TUTORIAL_STEPS = numberPhaseSteps(REPORTS_TUTORIAL_SEQUENCE, "table");
export const REPORT_DETAIL_TUTORIAL_STEPS = numberPhaseSteps(REPORTS_TUTORIAL_SEQUENCE, "detail");

const mockSeries = (start, days, base, phase) =>
  Array.from({ length: days }, (_, index) => {
    const sessions = Math.round(base + 40 * Math.sin(index * 0.85 + phase) + index * 1.5);
    return {
      date: `${start}-${String(index + 1).padStart(2, "0")}`,
      sessions,
      total_users: Math.round(sessions * 0.74),
      key_events: Math.round(sessions * 0.035),
      conversion_rate: 3.5,
    };
  });

export const REPORTS_TUTORIAL_MOCK_REPORT = {
  id: REPORTS_TUTORIAL_MOCK_ID,
  client_name: "Clínica Aurora",
  agency_name: "Agencia de ejemplo",
  agency_branding: { logo_url: null },
  period_start: "2026-03-01",
  period_end: "2026-03-31",
  period_label: "Marzo 2026",
  status: "generated",
  executive_summary:
    "Marzo ha sido un buen mes para Clínica Aurora: las sesiones crecen un 8 % y las conversiones un 18 %, con la reserva de cita online como principal objetivo. Se avanzó en el itinerario de SEO local y se aprobaron 8 piezas de contenido.",
  traffic_analysis:
    "El tráfico crece de forma sostenida durante todo el mes y las conversiones suben más del doble que las sesiones: llega tráfico más cualificado. El orgánico aporta casi la mitad de las visitas.",
  visibility_analysis:
    "Los clics orgánicos suben un 12 % y la posición media mejora casi dos puestos gracias a las nuevas fichas locales.",
  performance_analysis:
    "El rendimiento en móvil sube 6 puntos, hasta 78, gracias a la optimización de imágenes y de la carga inicial.",
  period_details:
    "La web gana tráfico más cualificado: las conversiones crecen por encima de las sesiones y el orgánico aporta casi la mitad de las visitas.\n\nSe cerró la fase de planificación del itinerario de SEO local y se optimizaron las fichas de Google Business Profile. La campaña de Meta Ads terminó con todos sus entregables.\n\nSe aprobaron 3 piezas de contenido, 2 de ellas de campaña, para Instagram, Facebook y el perfil de empresa.\n\nSe acordó medir los formularios con eventos propios y estrenar la nueva imagen de la home.",
  analytics_data: {
    connected: true,
    period: {
      start_date: "2026-03-01",
      end_date: "2026-03-31",
      previous_start_date: "2026-01-29",
      previous_end_date: "2026-02-28",
    },
    kpis: {
      users: { value: 6420, previous: 5980, change_percent: 7.4, change_points: null },
      sessions: { value: 8710, previous: 8065, change_percent: 8, change_points: null },
      key_events: { value: 305, previous: 258, change_percent: 18.2, change_points: null },
      conversion_rate: { value: 0.035, previous: 0.032, change_percent: null, change_points: 0.3 },
    },
    timeseries: {
      current: mockSeries("2026-03", 31, 280, 0),
      previous: mockSeries("2026-02", 31, 260, 1.9),
    },
    channels: [
      { channel: "Organic Search", sessions: 4050, share: 46.5 },
      { channel: "Direct", sessions: 2180, share: 25 },
      { channel: "Paid Social", sessions: 1390, share: 16 },
      { channel: "Referral", sessions: 1090, share: 12.5 },
    ],
    devices: [
      { name: "mobile", sessions: 6100, share_percent: 70 },
      { name: "desktop", sessions: 2260, share_percent: 26 },
      { name: "tablet", sessions: 350, share_percent: 4 },
    ],
    pages: [
      { path: "/", sessions: 2900 },
      { path: "/reservar-cita", sessions: 1240 },
      { path: "/tratamientos", sessions: 980 },
    ],
    key_events: [
      { event_name: "book_appointment", label: "Solicitudes de cita", count: 164, change_percent: 22.4 },
      { event_name: "click_to_call", label: "Llamadas desde la web", count: 92, change_percent: 15 },
      { event_name: "whatsapp_click", label: "Clics en WhatsApp", count: 49, change_percent: 8.9 },
    ],
  },
  seo_data: {
    connected: true,
    search_console: {
      connected: true,
      kpis: {
        clicks: { value: 1240, previous: 1103, change_percent: 12.4, change_points: null },
        impressions: { value: 38650, previous: 35754, change_percent: 8.1, change_points: null },
        ctr: { value: 0.0321, previous: 0.0309, change_percent: null, change_points: 0.12 },
        position: { value: 14.6, previous: 16.4, change_percent: null, change_points: 1.8 },
      },
      queries: [
        { query: "clínica dental aurora", clicks: 310, impressions: 2100, ctr: 0.1476, position: 1.4, position_change: 0.2 },
        { query: "dentista cerca de mí", clicks: 145, impressions: 6400, ctr: 0.0227, position: 7.8, position_change: 1.6 },
        { query: "implantes dentales precio", clicks: 88, impressions: 5200, ctr: 0.0169, position: 11.2, position_change: -0.6 },
      ],
      pages: [],
    },
    seranking: { connected: false },
  },
  pagespeed_data: {
    connected: true,
    strategies: {
      mobile: {
        kpis: {
          performance: { value: 78, change_points: 6 },
          accessibility: { value: 92, change_points: 0 },
          best_practices: { value: 96, change_points: 4 },
          seo: { value: 91, change_points: 2 },
        },
      },
      desktop: {
        kpis: {
          performance: { value: 94, change_points: 3 },
          accessibility: { value: 95, change_points: 1 },
          best_practices: { value: 100, change_points: 0 },
          seo: { value: 92, change_points: 2 },
        },
      },
    },
  },
  projects_data: {
    total_projects: 1,
    active_projects: 1,
    completed_projects: 0,
    projects: [
      {
        id: "project-1",
        name: "Setup SEO Local",
        status: "in_progress",
        is_recurrent: false,
        progress_percentage: 72,
        phases: [
          {
            id: "phase-1",
            name: "Planificación",
            tasks_count: 2,
            tasks_completed: 2,
            tasks: [
              { id: "task-1", name: "Definición de objetivos", status: "finalizado" },
              { id: "task-2", name: "Recogida de accesos", status: "finalizado" },
            ],
          },
          {
            id: "phase-2",
            name: "Implementación",
            tasks_count: 2,
            tasks_completed: 1,
            tasks: [
              { id: "task-3", name: "Optimización de fichas", status: "finalizado" },
              { id: "task-4", name: "Carga de categorías", status: "en_desarrollo" },
            ],
          },
        ],
      },
    ],
  },
  content_data: {
    total_approved: 3,
    campaign_pieces: 2,
    individual_pieces: 1,
    pieces: [
      { id: "piece-1", title: "Lanzamiento blanqueamiento dental", is_campaign: true, platforms: ["instagram", "facebook"], approved_at: "2026-09-04T10:00:00" },
      { id: "piece-2", title: "Consejos de higiene tras una limpieza", is_campaign: true, platforms: ["instagram"], approved_at: "2026-09-12T10:00:00" },
      { id: "piece-3", title: "Nuevo horario de tardes", is_campaign: false, platforms: ["google_business_profile", "facebook"], approved_at: "2026-09-21T10:00:00" },
    ],
  },
  conversation_metrics: {
    total_messages: 26,
    messages_by_client: 11,
    messages_by_team: 15,
  },
  tickets_data: {
    total_tickets: 3,
    total_time_consumed_minutes: 315,
    tickets: [
      { id: "ticket-1", title: "Corrección de tracking en formularios", status: "closed", time_consumed_minutes: 95 },
      { id: "ticket-2", title: "Ajuste visual en hero principal", status: "closed", time_consumed_minutes: 70 },
      { id: "ticket-3", title: "Revisión de etiquetas duplicadas", status: "open", time_consumed_minutes: 150 },
    ],
  },
};

export const ReportsTutorialTooltip = TutorialJoyrideTooltip;
export const getReportsTutorialStyles = getTutorialJoyrideStyles;
